import React, {
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type WalletWidgetProps,
  type WalletThemingProps,
  WALLET_THEMING_PROP_TO_PARAM,
} from "./types";

/**
 * Ref handle exposed by LegionWallet for imperative control.
 */
export interface LegionWalletHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildWalletWidgetUrl(props: WalletWidgetProps): string {
  const {
    baseUrl,
    userId,
    apiKey,
    theme = "light",
    showBalance = true,
    showHistory = true,
  } = props;

  const url = new URL("/widget/wallet", baseUrl);

  url.searchParams.set("userId", userId);
  url.searchParams.set("theme", theme);
  url.searchParams.set("showBalance", String(showBalance));
  url.searchParams.set("showHistory", String(showHistory));

  if (apiKey) {
    url.searchParams.set("apiKey", apiKey);
  }

  // Append theming params
  const themingKeys = Object.keys(WALLET_THEMING_PROP_TO_PARAM) as Array<
    keyof WalletThemingProps
  >;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(WALLET_THEMING_PROP_TO_PARAM[key], String(value));
    }
  }

  return url.toString();
}

/**
 * LegionWallet – a React component that embeds the Legion Wallet widget
 * via an iframe.
 *
 * @example
 * ```tsx
 * import { LegionWallet } from '@legionhandtech/lht-react-widgets';
 *
 * function App() {
 *   return (
 *     <LegionWallet
 *       baseUrl="https://your-legion-domain.com"
 *       userId="user-123"
 *       apiKey="your-api-key"
 *       theme="light"
 *     />
 *   );
 * }
 * ```
 */
export const LegionWallet = forwardRef<LegionWalletHandle, WalletWidgetProps>(
  function LegionWallet(props, ref) {
    const {
      theme = "light",
      userId,
      apiKey,
      showBalance,
      showHistory,
      height = "500px",
      className,
      style,
      onLoad,
      onError,
      baseUrl,
      ...rest
    } = props;

    const iframeRef = useRef<HTMLIFrameElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        refresh: () => {
          const iframe = iframeRef.current;
          if (iframe?.contentWindow) {
            iframe.contentWindow.location.reload();
          }
        },
      }),
      []
    );

    const src = useMemo(
      () => buildWalletWidgetUrl(props),
      [
        baseUrl,
        userId,
        apiKey,
        theme,
        showBalance,
        showHistory,
        ...Object.keys(WALLET_THEMING_PROP_TO_PARAM).map(
          (k) => rest[k as keyof typeof rest]
        ),
      ]
    );

    const handleLoad = useCallback(() => {
      // Send API key via postMessage
      if (apiKey && iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          { type: "AUTH_TOKEN", token: apiKey },
          baseUrl
        );
      }
      onLoad?.({ widget: "wallet", theme });
    }, [apiKey, baseUrl, theme, onLoad]);

    const handleError = useCallback(() => {
      onError?.("Failed to load wallet widget");
    }, [onError]);

    const containerStyle: React.CSSProperties = {
      display: "block",
      width: "100%",
      height,
      ...style,
    };

    const iframeStyle: React.CSSProperties = {
      border: "none",
      borderRadius: "8px",
      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
      width: "100%",
      height: "100%",
    };

    return (
      <div className={className} style={containerStyle}>
        <iframe
          ref={iframeRef}
          src={src}
          title="Wallet Widget"
          width="100%"
          height="100%"
          loading="lazy"
          style={iframeStyle}
          onLoad={handleLoad}
          onError={handleError}
        />
      </div>
    );
  }
);

